/**
 * Locations & Hours tab (spec 2026-07-22 §2.1/§2.3): CRUD for business
 * locations with per-location pickup/delivery capability and structured
 * weekly hours. Delivery hours are one owner-defined window — help copy:
 * "Need morning prep time? Start the window later."
 */

import { useCallback, useEffect, useState } from 'react'
import { Plus, Edit, Trash2, MapPin } from 'lucide-react'
import {
  Badge, Button, Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, EmptyState, Spinner, Table, TableBody, TableCell,
  TableHead, TableHeader, TableRow, useToast,
} from '@/components/ui'
import { apiClient } from '@/services/apiClient'
import type { BusinessLocation } from '@/types/commerce'
import { LocationFormDrawer } from './LocationFormDrawer'

function transform(p: Record<string, unknown>): BusinessLocation {
  return {
    id: p.id as string,
    userId: p.user_id as string,
    name: p.name as string,
    address: p.address as string,
    lat: (p.lat as number | null) ?? null,
    lng: (p.lng as number | null) ?? null,
    pickupEnabled: (p.pickup_enabled as boolean) ?? false,
    pickupHours: (p.pickup_hours as BusinessLocation['pickupHours']) ?? null,
    deliveryEnabled: (p.delivery_enabled as boolean) ?? false,
    deliveryHours: (p.delivery_hours as BusinessLocation['deliveryHours']) ?? null,
    deliveryCoverage: (p.delivery_coverage as string[] | null) ?? null,
    hoursText: (p.hours_text as string | null) ?? null,
    notes: (p.notes as string | null) ?? null,
    isActive: (p.is_active as boolean) ?? true,
    sortOrder: (p.sort_order as number) || 0,
    createdAt: (p.created_at as string | null) ?? null,
    updatedAt: (p.updated_at as string | null) ?? null,
  }
}

export function BusinessLocationsTab() {
  const [locations, setLocations] = useState<BusinessLocation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<BusinessLocation | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<BusinessLocation | null>(null)
  const [deleting, setDeleting] = useState(false)
  const { addToast } = useToast()

  const fetchLocations = useCallback(async () => {
    setIsLoading(true)
    try {
      const data = await apiClient.get('/api/commerce/business-locations')
      setLocations((data.items || []).map(transform))
    } catch {
      // silent
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => { fetchLocations() }, [fetchLocations])

  const openEdit = (l: BusinessLocation) => { setEditing(l); setShowForm(true) }

  const handleDelete = async (id: string) => {
    setDeleting(true)
    try {
      await apiClient.delete(`/api/commerce/business-locations/${id}`)
      setConfirmDelete(null)
      fetchLocations()
    } catch {
      addToast({ type: 'error', title: 'Failed to deactivate location' })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {locations.length} location{locations.length === 1 ? '' : 's'}
        </span>
        <Button size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />}
          onClick={() => { setEditing(null); setShowForm(true) }}>
          Add Location
        </Button>
      </div>

      <LocationFormDrawer
        open={showForm}
        location={editing}
        onClose={() => setShowForm(false)}
        onSaved={() => { setShowForm(false); setEditing(null); fetchLocations() }}
      />

      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : locations.length === 0 ? (
        <EmptyState
          icon={MapPin}
          title="No locations yet"
          description="Add your business locations to power pickup and delivery hours."
          action={
            <Button size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />}
              onClick={() => { setEditing(null); setShowForm(true) }}>
              Add your first location
            </Button>
          }
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Services</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {locations.map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="font-medium text-foreground">{l.name}</TableCell>
                  <TableCell className="max-w-md text-muted-foreground">{l.address}</TableCell>
                  <TableCell>
                    {l.pickupEnabled || l.deliveryEnabled ? (
                      <div className="flex gap-1.5">
                        {l.pickupEnabled && <Badge variant="secondary">Pickup</Badge>}
                        {l.deliveryEnabled && <Badge variant="secondary">Delivery</Badge>}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openEdit(l)} aria-label={`Edit ${l.name}`}
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                        <Edit className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => setConfirmDelete(l)} aria-label={`Deactivate ${l.name}`}
                        className="rounded-md p-1.5 text-destructive transition-colors hover:bg-destructive/10">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={confirmDelete !== null}
        onOpenChange={(o) => { if (!o) setConfirmDelete(null) }}>
        {confirmDelete && (
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Deactivate {confirmDelete.name}?</DialogTitle>
              <DialogDescription>
                The location is hidden from ordering and hours. You can recreate it later.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Button>
              <Button variant="destructive" isLoading={deleting}
                onClick={() => handleDelete(confirmDelete.id)}>
                Deactivate
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  )
}
